import React, { useState, useEffect } from 'react';
import { 
  Clock, MapPin, LogOut, CheckCircle2, LayoutDashboard, 
  FileText, ListTodo, Calendar, MessageSquare, TrendingUp,
  TreePine, Droplet, Wind, Zap, Eye, Bell, AlertCircle,
  Menu, X, Home, Download, Map, XCircle, Settings
} from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import InstallPrompt from '@/components/InstallPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeePortal = ({ user, onLogout, onSwitchView, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [sicknessRecords, setSicknessRecords] = useState([]);
  const [daysBooked, setDaysBooked] = useState(0);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [clockAction, setClockAction] = useState(null);
  const [note, setNote] = useState('');
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showTimeOffDialog, setShowTimeOffDialog] = useState(false);
  const [timeOffForm, setTimeOffForm] = useState({
    first_day: '',
    first_day_type: 'full_day',
    last_day: '',
    last_day_type: 'full_day',
    notes: ''
  });
  const [calculatedDays, setCalculatedDays] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [menuSettings, setMenuSettings] = useState({
    show_payslips: true,
    show_documents: true,
    show_impact: true,
    show_roadmap: true,
    show_surveys: true
  });
  const [announcementsTab, setAnnouncementsTab] = useState('announcements'); // 'announcements' | 'newsletters'
  const [newsletters, setNewsletters] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showNewsletterDialog, setShowNewsletterDialog] = useState(false);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [activeDriverLog, setActiveDriverLog] = useState(null);
  const [showDocumentsSection, setShowDocumentsSection] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentCategory, setDocumentCategory] = useState('Contract');
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showViewDocumentDialog, setShowViewDocumentDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [impactStats, setImpactStats] = useState(null);
  const [unreadSurveysCount, setUnreadSurveysCount] = useState(0);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [yearlyAllowance, setYearlyAllowance] = useState(null);
  const [selectedBookingYear, setSelectedBookingYear] = useState(null);
  
  // PWA Install functionality
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  
  // Only show driver features if department is 'drivers' AND role is not manager/admin
  const isDriver = profile?.department?.toLowerCase() === 'drivers' && 
                   profile?.role !== 'manager' && 
                   profile?.role !== 'admin';

  const handleInstallClick = async () => {
    if (!isInstallable) {
      // If not installable via the prompt, show platform-specific instructions
      toast.info('To install this app, use your browser\'s "Add to Home Screen" option');
      return;
    }
    
    const success = await promptInstall();
    if (success) {
      toast.success('App installed successfully!');
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/notifications/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotificationCount(data.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  };

  const fetchNotificationDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch notification details:', error);
    }
  };

  const fetchMenuSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings/employee-portal-menu/public`);
      if (res.ok) {
        const data = await res.json();
        setMenuSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch menu settings:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [profileRes, statusRes, requestsRes, sicknessRes, newslettersRes, announcementsRes, impactStatsRes, surveysRes, tasksRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/employee/profile`, { headers }),
        fetch(`${BACKEND_URL}/api/employee/status`, { headers }),
        fetch(`${BACKEND_URL}/api/time-off-requests/my-requests`, { headers }),
        fetch(`${BACKEND_URL}/api/sickness/my-records`, { headers }),
        fetch(`${BACKEND_URL}/api/newsletters/employee/available`, { 
          headers: {
            ...headers,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
          cache: 'no-store'
        }),
        fetch(`${BACKEND_URL}/api/announcements/employee/available`, { 
          headers: {
            ...headers,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
          cache: 'no-store'
        }),
        fetch(`${BACKEND_URL}/api/impact/stats`, { 
          headers: {
            ...headers,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
          cache: 'no-store'
        }),
        fetch(`${BACKEND_URL}/api/surveys/employee/pending`, { headers }),
        fetch(`${BACKEND_URL}/api/tasks/employee/pending-count`, { headers })
      ]);

      const profileData = profileRes.ok ? await profileRes.json() : null;
      const statusData = statusRes.ok ? await statusRes.json() : null;
      const requestsData = requestsRes.ok ? await requestsRes.json() : [];
      const sicknessData = sicknessRes.ok ? await sicknessRes.json() : [];
      const newslettersData = newslettersRes.ok ? await newslettersRes.json() : [];
      const announcementsData = announcementsRes.ok ? await announcementsRes.json() : [];
      const impactStatsData = impactStatsRes.ok ? await impactStatsRes.json() : null;
      const surveysData = surveysRes.ok ? await surveysRes.json() : [];
      const tasksData = tasksRes.ok ? await tasksRes.json() : { count: 0 };

      // Debug logging for announcements
      console.log('Announcements response status:', announcementsRes.status);
      console.log('Announcements data:', announcementsData);

      setProfile(profileData);
      setStatus(statusData);
      setTimeOffRequests(Array.isArray(requestsData) ? requestsData : []);
      setSicknessRecords(Array.isArray(sicknessData) ? sicknessData : []);
      setNewsletters(Array.isArray(newslettersData) ? newslettersData : []);
      setAnnouncements(Array.isArray(announcementsData) ? announcementsData : []);
      setImpactStats(impactStatsData);
      setUnreadSurveysCount(Array.isArray(surveysData) ? surveysData.length : 0);
      setPendingTasksCount(tasksData.count || 0);

      // Fetch current year's leave allowance
      if (profileData?.id) {
        const currentYear = new Date().getFullYear();
        try {
          console.log('Fetching leave allowance for employee:', profileData.id, 'year:', currentYear);
          const allowanceRes = await fetch(`${BACKEND_URL}/api/leave-allowances/${profileData.id}?year=${currentYear}`, { headers });
          console.log('Leave allowance response status:', allowanceRes.status);
          if (allowanceRes.ok) {
            const allowanceData = await allowanceRes.json();
            console.log('Leave allowance data:', allowanceData);
            setYearlyAllowance(allowanceData);
          } else {
            const errorText = await allowanceRes.text();
            console.error('Leave allowance API error:', allowanceRes.status, errorText);
          }
        } catch (error) {
          console.error('Error fetching leave allowance:', error);
        }
      } else {
        console.warn('No profileData.id available for leave allowance fetch');
      }

      // If user is a driver, also fetch their active log status
      if (profileData?.department?.toLowerCase() === 'drivers') {
        const scheduleRes = await fetch(`${BACKEND_URL}/api/driver-schedules/my-schedules`, { headers });
        const scheduleData = await scheduleRes.json();
        setActiveDriverLog(scheduleData.active_log);
      }
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletterClick = async (newsletter) => {
    setSelectedNewsletter(newsletter);
    setShowNewsletterDialog(true);
    
    // Mark as viewed if it's new
    if (newsletter.is_new) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${BACKEND_URL}/api/newsletters/${newsletter.id}/mark-viewed`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Update local state
        setNewsletters(newsletters.map(n => 
          n.id === newsletter.id ? { ...n, is_new: false } : n
        ));
      } catch (error) {
        console.error('Error marking newsletter as viewed:', error);
      }
    }
  };

  const handleAnnouncementClick = async (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowAnnouncementDialog(true);
    
    // Mark as viewed if it's new
    if (announcement.is_new) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${BACKEND_URL}/api/announcements/${announcement.id}/mark-viewed`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Update local state
        setAnnouncements(announcements.map(a => 
          a.id === announcement.id ? { ...a, is_new: false } : a
        ));
      } catch (error) {
        console.error('Error marking announcement as viewed:', error);
      }
    }
  };

  const calculateDays = async () => {
    if (!timeOffForm.first_day || !timeOffForm.last_day) {
      setCalculatedDays(null);
      setYearlyAllowance(null);
      setSelectedBookingYear(null);
      return;
    }

    const firstDate = new Date(timeOffForm.first_day);
    const lastDate = new Date(timeOffForm.last_day);

    if (lastDate < firstDate) {
      setCalculatedDays(null);
      setYearlyAllowance(null);
      setSelectedBookingYear(null);
      return;
    }

    // Determine the year of the booking (use first day's year)
    const bookingYear = firstDate.getFullYear();
    setSelectedBookingYear(bookingYear);

    // Fetch yearly allowance if year changed or not loaded
    if (!yearlyAllowance || yearlyAllowance.year !== bookingYear) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/leave-allowances/${profile?.id}?year=${bookingYear}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setYearlyAllowance(data);
        }
      } catch (error) {
        console.error('Error fetching yearly allowance:', error);
      }
    }

    // Simple calculation - will be validated by backend
    let totalDays = 0;
    const currentDate = new Date(firstDate);

    while (currentDate <= lastDate) {
      // Skip weekends
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        if (currentDate.getTime() === firstDate.getTime()) {
          totalDays += timeOffForm.first_day_type === 'full_day' ? 1 : timeOffForm.first_day_type === 'half_day' ? 0.5 : 0.25;
        } else if (currentDate.getTime() === lastDate.getTime()) {
          totalDays += timeOffForm.last_day_type === 'full_day' ? 1 : timeOffForm.last_day_type === 'half_day' ? 0.5 : 0.25;
        } else {
          totalDays += 1;
        }
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setCalculatedDays(totalDays);
  };

  const handleTimeOffSubmit = async () => {
    if (!timeOffForm.first_day || !timeOffForm.last_day) {
      toast.error('Please select both start and end dates');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/time-off-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(timeOffForm)
      });

      if (!res.ok) {
        throw new Error('Failed to submit request');
      }

      toast.success('Time off request submitted successfully!');
      setShowTimeOffDialog(false);
      setTimeOffForm({
        first_day: '',
        first_day_type: 'full_day',
        last_day: '',
        last_day_type: 'full_day',
        notes: ''
      });
      setCalculatedDays(null);
      setYearlyAllowance(null);
      setSelectedBookingYear(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit time off request');
    }
  };

  const fetchDocuments = async () => {
    if (!profile?.id) return;
    
    setLoadingDocuments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/documents/employee/${profile.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/documents/${documentId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Document downloaded successfully');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setShowViewDocumentDialog(true);
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this time off request?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/time-off-requests/${requestId}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel request');
      }

      toast.success('Time off request cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  useEffect(() => {
    fetchData();
    fetchNotificationCount();
    fetchMenuSettings();
    const interval = setInterval(() => {
      fetchData();
      fetchNotificationCount();
    }, 30000);
    
    // Listen for notification refresh events from child components
    const handleRefreshNotifications = () => {
      fetchNotificationCount();
      if (showNotifications) {
        fetchNotificationDetails();
      }
    };
    window.addEventListener('refreshNotifications', handleRefreshNotifications);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshNotifications', handleRefreshNotifications);
    };
  }, [showNotifications]);

  // Calculate days booked for CURRENT YEAR only
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    let total = 0;
    
    // Add approved time-off requests for current year only
    const approvedRequests = timeOffRequests.filter(req => {
      if (req.status !== 'approved') return false;
      // Check if the request is for current year (using first_day)
      const requestYear = new Date(req.first_day).getFullYear();
      return requestYear === currentYear;
    });
    total += approvedRequests.reduce((sum, req) => sum + (req.total_days || 0), 0);
    
    // Add sickness holiday deductions for current year (completed sickness with allowance deduction)
    const sicknessHolidays = sicknessRecords.filter(record => {
      if (record.status !== 'completed' || (record.total_allowance_deduction || 0) <= 0) return false;
      // Check if the sickness is for current year
      const sicknessYear = new Date(record.start_date).getFullYear();
      return sicknessYear === currentYear;
    });
    total += sicknessHolidays.reduce((sum, record) => sum + (record.total_allowance_deduction || 0), 0);
    
    setDaysBooked(total);
  }, [timeOffRequests, sicknessRecords]);

  useEffect(() => {
    calculateDays();
  }, [timeOffForm.first_day, timeOffForm.last_day, timeOffForm.first_day_type, timeOffForm.last_day_type]);

  // Refresh data when returning from driver log
  useEffect(() => {
    if (location.state?.refreshData) {
      fetchData();
      // Clear the state to prevent repeated refreshes
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const handleClockButtonClick = () => {
    const action = status?.clocked_in ? 'out' : 'in';
    setClockAction(action);
    
    if (action === 'out') {
      setShowNoteDialog(true);
    } else {
      setShowLocationDialog(true);
    }
  };

  const handleLocationSelect = async (location) => {
    setShowLocationDialog(false);
    await performClockAction('in', '', location);
  };

  const handleClockAction = async (action) => {
    if (action === 'out') {
      setShowNoteDialog(true);
      return;
    }

    setShowLocationDialog(true);
  };

  const performClockAction = async (action, noteText = '', selectedLocation = 'Working from Home') => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/employee/clock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: action,
          location: selectedLocation,
          note: noteText || undefined
        })
      });

      const data = await res.json();

      if (data.error) {
        toast.error(data.message);
        return;
      }

      if (data.success) {
        setConfirmationData({
          action: data.action,
          timestamp: new Date(data.timestamp).toLocaleString(),
          location: data.location,
          employee_name: data.employee_name
        });
        setShowConfirmation(true);
        
        // Refresh status immediately so button updates
        fetchData();
        
        setTimeout(() => {
          setShowConfirmation(false);
          setNote('');
        }, 5000);
      }
    } catch (error) {
      toast.error('Clock action failed. Please try again.');
    }
  };

  const handleNoteSubmit = async () => {
    setShowNoteDialog(false);
    await performClockAction('out', note, status?.location || 'Working from Home');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-teal-600 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex overflow-x-hidden" style={{ backgroundColor: '#F8FAFC' }}>
      {/* Persistent Sidebar - Desktop */}
      <div className="hidden lg:flex lg:flex-col lg:w-72 lg:fixed lg:inset-y-0 bg-white border-r border-gray-200 z-40">
        {/* Sidebar Header */}
        <div className="h-16 px-6 border-b border-gray-200 flex items-center">
          <img src="/Primary-Full-Colour-RGB.svg" alt="Reuseabox" className="h-7 w-auto object-contain" />
        </div>

        {/* Menu Items */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="px-3 space-y-1">
            <Link
              to="/profile"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                location.pathname === '/profile' 
                  ? 'bg-emerald-50 text-emerald-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
              }`}
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </Link>
            <button
              onClick={() => setShowTimeOffDialog(true)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-800 w-full text-left"
            >
              <Calendar className="w-5 h-5" />
              Book Time Off
            </button>
            {menuSettings.show_payslips && (
              <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-800 w-full text-left">
                <FileText className="w-5 h-5" />
                Payslips
                <span className="ml-auto text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Soon</span>
              </button>
            )}
            {menuSettings.show_documents && (
              <Link
                to="/profile/documents"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                  location.pathname === '/profile/documents' 
                    ? 'bg-emerald-50 text-emerald-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                }`}
              >
                <FileText className="w-5 h-5" />
                Documents
              </Link>
            )}
            {menuSettings.show_impact && (
              <Link
                to="/profile/impact-page"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                  location.pathname === '/profile/impact-page' 
                    ? 'bg-emerald-50 text-emerald-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                }`}
              >
                <TreePine className="w-5 h-5" />
                Our Impact to Date
              </Link>
            )}
            {menuSettings.show_roadmap && (
              <Link
                to="/profile/roadmap"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                  location.pathname === '/profile/roadmap' 
                    ? 'bg-emerald-50 text-emerald-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                }`}
              >
                <Map className="w-5 h-5" />
                2026 Roadmap
              </Link>
            )}
            
            {/* Notifications Settings */}
            <Link
              to="/profile/notification-settings"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                location.pathname === '/profile/notification-settings' 
                  ? 'bg-emerald-50 text-emerald-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
              }`}
            >
              <Bell className="w-5 h-5" />
              Notifications
            </Link>
            
            {menuSettings.show_surveys && (
              <Link
                to="/profile/surveys"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                  location.pathname === '/profile/surveys' 
                    ? 'bg-emerald-50 text-emerald-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                }`}
              >
                <MessageSquare className="w-5 h-5" />
                Surveys
              </Link>
            )}
            {user?.role === 'manager' && onSwitchView && (
              <>
                <div className="my-3 border-t border-gray-200" />
                <button
                  onClick={() => {
                    onSwitchView();
                    navigate('/admin/dashboard');
                  }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium w-full text-left"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  Managers Portal
                </button>
              </>
            )}
          </nav>
        </div>

        {/* Bottom Menu Items - User Profile */}
        <div className="border-t border-gray-200 p-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold text-base flex-shrink-0">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
                {user?.first_name} {user?.last_name}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-xs text-gray-500 truncate">
                  {user?.job_title || 'No Job Title'}
                </p>
                <span className="text-gray-300 text-xs">|</span>
                <button
                  onClick={onLogout}
                  className="text-xs text-gray-500 hover:text-red-600 transition-colors flex items-center gap-1"
                  title="Logout"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-72 flex flex-col bg-gray-50">
        {/* Desktop Header Bar */}
        <div className="hidden lg:flex items-center justify-between sticky top-0 z-30 bg-white border-b border-gray-200 h-16 px-6">
          {/* Logo */}
          <div className="flex-1">
            <img 
              src="/Primary-Full-Colour-RGB.svg" 
              alt="Reuseabox" 
              className="h-7 w-auto object-contain"
            />
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) fetchNotificationDetails();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                title="Notifications"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {notificationCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowNotifications(false)} />
                  <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-40 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                      <button
                        onClick={async () => {
                          try {
                            const res = await fetch(`${BACKEND_URL}/api/notifications/clear-all`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                            });
                            if (res.ok) {
                              setNotifications([]);
                              setNotificationCount(0);
                              await fetchNotificationCount();
                              await fetchNotificationDetails();
                            }
                          } catch (error) {
                            console.error('Failed to clear notifications:', error);
                          }
                        }}
                        disabled={notifications.length === 0}
                        className={`text-sm font-medium ${notifications.length > 0 ? 'text-teal-600 hover:text-teal-700' : 'text-gray-400 cursor-not-allowed'}`}
                      >
                        Clear All
                      </button>
                    </div>
                    {notifications.length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {notifications.map((notif) => (
                          <div key={notif.id} className="p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                                <ListTodo className="w-5 h-5 text-teal-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{notif.message}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {notif.created_at && new Date(notif.created_at).toLocaleString()}
                                </p>
                                {notif.task_id && (
                                  <button
                                    onClick={() => {
                                      navigate('/profile/tasks');
                                      setShowNotifications(false);
                                    }}
                                    className="mt-2 text-sm font-medium text-teal-600 hover:text-teal-700"
                                  >
                                    View Task →
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">No new notifications</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Settings">
              <Settings className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={onLogout}
              className="ml-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="menu-toggle-button"
                onClick={() => setShowMenu(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
              <img 
                src="/Primary-Full-Colour-RGB.svg" 
                alt="Reuseabox" 
                className="h-6 w-auto object-contain"
              />
            </div>
            <button
              onClick={onLogout}
              className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

      {/* Side Menu */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 transition-opacity"
            onClick={() => setShowMenu(false)}
            data-testid="menu-backdrop"
          />
          
          {/* Menu Drawer */}
          <div className="fixed top-0 left-0 h-full w-72 bg-white shadow-2xl z-50 transform transition-transform overflow-y-auto" data-testid="side-menu">
            <div className="flex flex-col min-h-full">
              {/* Sidebar Header */}
              <div className="h-16 px-6 border-b border-gray-200 flex items-center justify-between">
                <img src="/Primary-Full-Colour-RGB.svg" alt="Reuseabox" className="h-7 w-auto object-contain" />
                <button
                  data-testid="menu-close-button"
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Menu Items */}
              <div className="flex-1 overflow-y-auto py-4">
                <nav className="px-3 space-y-1">
                  <Link
                    to="/profile"
                    data-testid="menu-dashboard"
                    onClick={() => setShowMenu(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                      location.pathname === '/profile' 
                        ? 'bg-emerald-50 text-emerald-700 font-medium' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                    }`}
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    Dashboard
                  </Link>

                  <button
                    data-testid="menu-book-time-off"
                    onClick={() => {
                      setShowTimeOffDialog(true);
                      setShowMenu(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-800 w-full text-left"
                  >
                    <Calendar className="w-5 h-5" />
                    Book Time Off
                  </button>

                  {menuSettings.show_payslips && (
                    <button
                      data-testid="menu-payslips"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-800 w-full text-left"
                    >
                      <FileText className="w-5 h-5" />
                      Payslips
                      <span className="ml-auto text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Soon</span>
                    </button>
                  )}

                  {menuSettings.show_documents && (
                    <Link
                      to="/profile/documents"
                      data-testid="menu-documents"
                      onClick={() => setShowMenu(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/profile/documents' 
                          ? 'bg-emerald-50 text-emerald-700 font-medium' 
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                      }`}
                    >
                      <FileText className="w-5 h-5" />
                      Documents
                    </Link>
                  )}

                  {menuSettings.show_impact && (
                    <Link
                      to="/profile/impact-page"
                      data-testid="menu-our-impact"
                      onClick={() => setShowMenu(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/profile/impact-page' 
                          ? 'bg-emerald-50 text-emerald-700 font-medium' 
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                      }`}
                    >
                      <TreePine className="w-5 h-5" />
                      Our Impact to Date
                    </Link>
                  )}

                  {menuSettings.show_roadmap && (
                    <Link
                      to="/profile/roadmap"
                      data-testid="menu-2026-roadmap"
                      onClick={() => setShowMenu(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        location.pathname === '/profile/roadmap' 
                          ? 'bg-emerald-50 text-emerald-700 font-medium' 
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                      }`}
                    >
                      <Map className="w-5 h-5" />
                      2026 Roadmap
                    </Link>
                  )}

                  {/* Notifications Settings */}
                  <Link
                    to="/profile/notification-settings"
                    data-testid="menu-notifications"
                    onClick={() => setShowMenu(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                      location.pathname === '/profile/notification-settings' 
                        ? 'bg-emerald-50 text-emerald-700 font-medium' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                    }`}
                  >
                    <Bell className="w-5 h-5" />
                    Notifications
                  </Link>

                  {menuSettings.show_surveys && (
                    <Link
                      to="/profile/surveys"
                      data-testid="menu-surveys"
                      onClick={() => setShowMenu(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm relative ${
                        location.pathname === '/profile/surveys' 
                          ? 'bg-emerald-50 text-emerald-700 font-medium' 
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                      }`}
                    >
                      <MessageSquare className="w-5 h-5" />
                      Surveys
                      {unreadSurveysCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                          {unreadSurveysCount}
                        </span>
                      )}
                    </Link>
                  )}

                  {user?.role === 'manager' && onSwitchView && (
                    <>
                      <div className="my-3 border-t border-gray-200" />
                      <button
                        data-testid="menu-managers-portal"
                        onClick={() => {
                          onSwitchView();
                          navigate('/admin/dashboard');
                          setShowMenu(false);
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium w-full text-left"
                      >
                        <LayoutDashboard className="w-5 h-5" />
                        Managers Portal
                      </button>
                    </>
                  )}
                </nav>
              </div>

              {/* Bottom Menu Items - User Profile */}
              <div className="border-t border-gray-200 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold text-base">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <p className="text-xs text-gray-500 truncate">
                        {user?.job_title || 'No Job Title'}
                      </p>
                      <span className="text-gray-300 text-xs">|</span>
                      <button
                        data-testid="menu-logout"
                        onClick={() => {
                          setShowMenu(false);
                          onLogout();
                        }}
                        className="text-xs text-gray-500 hover:text-red-600 transition-colors flex items-center gap-1"
                        title="Logout"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

        {/* Main Content */}
        <div className="max-w-7xl mx-auto py-4 sm:py-8 px-4 sm:px-6">
          {children ? children : (
        !showConfirmation ? (
          <>
            {/* Clock In/Out Button or View Schedules for Drivers */}
            <div className="mb-5">
              {isDriver ? (
                <Button
                  data-testid="view-schedules-button"
                  onClick={() => navigate('/profile/driver-schedules')}
                  className={`w-full h-14 text-lg font-semibold shadow-md hover:shadow-lg transition-all ${
                    activeDriverLog 
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600' 
                      : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
                  }`}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  {activeDriverLog ? 'Update Driver Log' : 'View Schedules'}
                </Button>
              ) : (
                <Button
                  data-testid="main-clock-button"
                  onClick={handleClockButtonClick}
                  className={`w-full h-14 text-lg font-semibold shadow-md hover:shadow-lg transition-all ${
                    status?.clocked_in
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                      : 'bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700'
                  }`}
                >
                  <Clock className="w-5 h-5 mr-2" />
                  {status?.clocked_in ? 'Clock Out' : 'Clock In'}
                  {status?.clocked_in && (
                    <span className="ml-3 text-sm opacity-90">
                      | {status.elapsed_hours.toFixed(1)} hrs @ {status.location}
                    </span>
                  )}
                </Button>
              )}
            </div>

            {/* Tasks | Surveys - 2 Column Grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
              <button
                data-testid="tasks-button"
                onClick={() => navigate('/profile/tasks')}
                className="bg-gradient-to-r from-orange-500 to-red-500 rounded-full py-3.5 px-4 sm:px-6 shadow-md hover:shadow-lg transition-all relative"
              >
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <ListTodo className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                  <span className="text-sm sm:text-base font-semibold text-white">Tasks</span>
                </div>
                {pendingTasksCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-white">
                    {pendingTasksCount}
                  </span>
                )}
              </button>

              <button
                data-testid="surveys-button"
                onClick={() => navigate('/profile/surveys')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full py-3.5 px-4 sm:px-6 shadow-md hover:shadow-lg transition-all relative"
              >
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                  <span className="text-sm sm:text-base font-semibold text-white">Surveys</span>
                </div>
                {unreadSurveysCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-white">
                    {unreadSurveysCount}
                  </span>
                )}
              </button>
            </div>

            {/* Payslips - 1 Column */}
            <div className="mb-4">
              <button
                data-testid="payslips-button"
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full py-3.5 px-4 sm:px-6 shadow-md hover:shadow-lg transition-all relative"
              >
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                  <span className="text-sm sm:text-base font-semibold text-white">Payslips</span>
                </div>
                <span className="absolute top-2 right-3 text-xs bg-white text-blue-600 px-2 py-0.5 rounded-full font-semibold">Coming Soon</span>
              </button>
            </div>

            {/* Book Time Off | Manage Time Off - 2 Column Grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5">
              <button
                data-testid="book-time-off-button"
                onClick={() => {
                  console.log('Book Time Off button clicked, setting showTimeOffDialog to true');
                  setShowTimeOffDialog(true);
                }}
                className="bg-gradient-to-r from-emerald-600 to-teal-500 rounded-full py-3.5 px-4 sm:px-6 shadow-md hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                  <span className="text-sm sm:text-base font-semibold text-white whitespace-nowrap">Book Time Off</span>
                </div>
              </button>

              <button
                data-testid="manage-time-off-button"
                onClick={() => navigate('/profile/manage-time-off')}
                className="bg-gradient-to-r from-teal-600 to-cyan-500 rounded-full py-3.5 px-4 sm:px-6 shadow-md hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                  <span className="text-sm sm:text-base font-semibold text-white whitespace-nowrap">Manage Time Off</span>
                </div>
              </button>
            </div>

            {/* Team Calendar Button - Full Width */}
            <button
              data-testid="team-calendar-button"
              onClick={() => navigate('/employee/calendar')}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-500 rounded-full py-3.5 px-4 sm:px-6 shadow-md hover:shadow-lg transition-all mb-5"
            >
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                <span className="text-sm sm:text-base font-semibold text-white">Team Calendar</span>
              </div>
            </button>

            {/* 2-Column Stats (removed Bradford Score) */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5">
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-md" data-testid="days-booked-tile">
                <div className="flex items-center justify-center mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
                  </div>
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-center text-gray-800">
                  {yearlyAllowance ? yearlyAllowance.days_used : daysBooked}
                </p>
                <p className="text-xs sm:text-sm text-center text-gray-500 mt-1">Days Booked ({new Date().getFullYear()})</p>
              </div>

              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-md" data-testid="days-left-tile">
                <div className="flex items-center justify-center mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
                  </div>
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-center text-gray-800">
                  {yearlyAllowance ? yearlyAllowance.days_remaining : ((profile?.holiday_entitlement_days || 28) - daysBooked)}
                </p>
                <p className="text-xs sm:text-sm text-center text-gray-500 mt-1">Days Left ({new Date().getFullYear()})</p>
              </div>
            </div>

            {/* Our Impact Tile */}
            {impactStats && (
              <div className="bg-gradient-to-br from-emerald-700 to-teal-600 rounded-2xl p-4 sm:p-6 shadow-lg mb-5" data-testid="our-impact-tile">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <TreePine className="w-4 h-4 sm:w-5 sm:h-5" />
                  Our Impact to Date
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
                    <p className="text-xl sm:text-2xl font-bold text-white mb-1">{impactStats.boxes_reused}</p>
                    <p className="text-teal-100 text-[10px] sm:text-xs leading-tight">Boxes Reused</p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
                    <p className="text-xl sm:text-2xl font-bold text-white mb-1">{impactStats.trees_saved}</p>
                    <p className="text-teal-100 text-[10px] sm:text-xs leading-tight">Trees Saved</p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
                    <p className="text-xl sm:text-2xl font-bold text-white mb-1">{impactStats.water_saved}</p>
                    <p className="text-teal-100 text-[10px] sm:text-xs leading-tight">Litres Water Saved</p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
                    <p className="text-xl sm:text-2xl font-bold text-white mb-1">{impactStats.co2_saved}</p>
                    <p className="text-teal-100 text-[10px] sm:text-xs leading-tight">Tonnes CO₂ Saved</p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
                    <p className="text-xl sm:text-2xl font-bold text-white mb-1">{impactStats.energy_saved}</p>
                    <p className="text-teal-100 text-[10px] sm:text-xs leading-tight">KwH Energy Saved</p>
                  </div>

                  {impactStats.show_full_impact_button && (
                    <button
                      data-testid="see-full-impact-button"
                      onClick={() => navigate('/profile/impact-page')}
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center transition-all"
                    >
                      <p className="text-sm sm:text-base font-semibold text-white mb-1">View Details</p>
                      <p className="text-teal-100 text-[10px] sm:text-xs leading-tight">See full impact →</p>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Company Announcements & Newsletters */}
            <div className="bg-white rounded-2xl p-6 shadow-md" data-testid="company-announcements-tile">
              <h3 className="text-lg font-bold text-emerald-900 mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-teal-600" />
                Company Updates
              </h3>
              
              {/* Tabs */}
              <div className="flex border-b mb-4">
                <button
                  onClick={() => setAnnouncementsTab('announcements')}
                  className={`pb-2 px-4 font-medium transition-colors ${
                    announcementsTab === 'announcements'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Announcements
                </button>
                <button
                  onClick={() => setAnnouncementsTab('newsletters')}
                  className={`pb-2 px-4 font-medium transition-colors relative ${
                    announcementsTab === 'newsletters'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Newsletters
                  {newsletters?.filter(n => n.is_new).length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {newsletters.filter(n => n.is_new).length}
                    </span>
                  )}
                </button>
              </div>

              {/* Announcements Tab Content */}
              {announcementsTab === 'announcements' && (
                <div className="space-y-3">
                  {announcements?.length > 0 ? (
                    announcements.map(announcement => {
                      const priorityStyles = {
                        high: { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-900', icon: 'text-red-600' },
                        medium: { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-900', icon: 'text-yellow-600' },
                        low: { bg: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-900', icon: 'text-blue-600' }
                      };
                      const style = priorityStyles[announcement.priority];
                      
                      return (
                        <div
                          key={announcement.id}
                          onClick={() => handleAnnouncementClick(announcement)}
                          className={`p-3 ${style.bg} border-l-4 ${style.border} rounded-lg cursor-pointer hover:opacity-80 transition-opacity`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 flex-1">
                              <AlertCircle className={`w-4 h-4 ${style.icon} mt-0.5 flex-shrink-0`} />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className={`font-semibold ${style.text} text-sm`}>{announcement.title}</p>
                                  {announcement.is_new && (
                                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">New</span>
                                  )}
                                </div>
                                <p className={`text-xs ${style.text} opacity-90 mt-1 line-clamp-2`}>
                                  {announcement.content}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs bg-white bg-opacity-50 px-2 py-0.5 rounded">
                                    {announcement.category}
                                  </span>
                                  <span className={`text-xs ${style.text} opacity-75`}>
                                    {new Date(announcement.published_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No announcements yet
                    </div>
                  )}
                </div>
              )}

              {/* Newsletters Tab Content */}
              {announcementsTab === 'newsletters' && (
                <div className="space-y-3">
                  {newsletters?.length > 0 ? (
                    newsletters.map(newsletter => (
                      <div
                        key={newsletter.id}
                        onClick={() => handleNewsletterClick(newsletter)}
                        className="p-3 bg-teal-50 border-l-4 border-teal-500 rounded-lg cursor-pointer hover:bg-teal-100 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1">
                            <FileText className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-teal-900 text-sm">{newsletter.title}</p>
                                {newsletter.is_new && (
                                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">New</span>
                                )}
                              </div>
                              {newsletter.summary && (
                                <p className="text-xs text-teal-700 mt-1">{newsletter.summary}</p>
                              )}
                              <p className="text-xs text-teal-600 mt-1">
                                Published {new Date(newsletter.published_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No newsletters yet
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Confirmation Screen */
          <div className="bg-white rounded-3xl shadow-2xl p-12 text-center animate-fade-in" data-testid="confirmation-screen">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-emerald-900 mb-4" data-testid="confirmation-action">
              {confirmationData?.action === 'clock_in' ? 'Clocked In Successfully!' : 'Clocked Out Successfully!'}
            </h2>
            <p className="text-xl text-emerald-600 mb-6" data-testid="confirmation-name">{confirmationData?.employee_name}</p>
            <div className="inline-block px-8 py-4 bg-emerald-100 rounded-xl mb-4">
              <p className="text-emerald-600 text-sm font-medium mb-1">Location</p>
              <p className="text-2xl font-bold text-emerald-900" data-testid="confirmation-location">{confirmationData?.location}</p>
            </div>
            <div className="inline-block px-8 py-4 bg-teal-100 rounded-xl">
              <p className="text-teal-600 text-sm font-medium mb-1">Timestamp</p>
              <p className="text-2xl font-bold text-teal-900" data-testid="confirmation-timestamp">{confirmationData?.timestamp}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>Add a Note (Optional)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              data-testid="note-textarea"
              placeholder="e.g., end of shift, leaving early..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
            />
            <div className="flex gap-3">
              <Button
                data-testid="note-skip-button"
                onClick={() => {
                  setShowNoteDialog(false);
                  performClockAction('out', '');
                }}
                variant="outline"
                className="flex-1"
              >
                Skip
              </Button>
              <Button
                data-testid="note-submit-button"
                onClick={handleNoteSubmit}
                className="flex-1 bg-teal-500 hover:bg-teal-600"
              >
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle>Select Your Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3">
              <Button
                data-testid="location-home-button"
                onClick={() => handleLocationSelect('Working from Home')}
                variant="outline"
                className="h-12 justify-start"
              >
                <MapPin className="w-5 h-5 mr-3" />
                Working from Home
              </Button>
              <Button
                data-testid="location-road-button"
                onClick={() => handleLocationSelect('On the Road')}
                variant="outline"
                className="h-12 justify-start"
              >
                <MapPin className="w-5 h-5 mr-3" />
                On the Road
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Time Off Dialog */}
      <Dialog open={showTimeOffDialog} onOpenChange={setShowTimeOffDialog}>
        <DialogContent className="sm:max-w-[500px] z-[9999]" data-testid="time-off-dialog">
          <DialogHeader>
            <DialogTitle>Book Time Off</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* First Day */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_day">First Day</Label>
                <Input
                  id="first_day"
                  type="date"
                  data-testid="first-day-input"
                  value={timeOffForm.first_day}
                  onChange={(e) => setTimeOffForm({...timeOffForm, first_day: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="first_day_type">Type</Label>
                <select
                  id="first_day_type"
                  data-testid="first-day-type-select"
                  value={timeOffForm.first_day_type}
                  onChange={(e) => setTimeOffForm({...timeOffForm, first_day_type: e.target.value})}
                  className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="full_day">Full Day</option>
                  <option value="half_day">Half Day</option>
                  <option value="quarter_day">Quarter Day</option>
                </select>
              </div>
            </div>

            {/* Last Day */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="last_day">Last Day</Label>
                <Input
                  id="last_day"
                  type="date"
                  data-testid="last-day-input"
                  value={timeOffForm.last_day}
                  onChange={(e) => setTimeOffForm({...timeOffForm, last_day: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="last_day_type">Type</Label>
                <select
                  id="last_day_type"
                  data-testid="last-day-type-select"
                  value={timeOffForm.last_day_type}
                  onChange={(e) => setTimeOffForm({...timeOffForm, last_day_type: e.target.value})}
                  className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="full_day">Full Day</option>
                  <option value="half_day">Half Day</option>
                  <option value="quarter_day">Quarter Day</option>
                </select>
              </div>
            </div>

            {/* Total Days Display */}
            {calculatedDays !== null && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4" data-testid="calculated-days">
                <p className="text-sm text-teal-700 mb-1">
                  Total days requested {selectedBookingYear && selectedBookingYear !== new Date().getFullYear() && (
                    <span className="font-medium">(for {selectedBookingYear})</span>
                  )}:
                </p>
                <p className="text-2xl font-bold text-teal-900">
                  {calculatedDays} / {yearlyAllowance ? yearlyAllowance.days_remaining : ((profile?.holiday_entitlement_days || 0) - daysBooked)} days left
                </p>
                {yearlyAllowance && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedBookingYear} Allowance: {yearlyAllowance.total_allowance} total ({yearlyAllowance.base_allowance} base + {yearlyAllowance.extra_days_total || 0} extra + {yearlyAllowance.carried_over_days || 0} carried)
                  </p>
                )}
                {calculatedDays > (yearlyAllowance ? yearlyAllowance.days_remaining : ((profile?.holiday_entitlement_days || 0) - daysBooked)) && (
                  <p className="text-xs text-red-600 mt-2">
                    ⚠️ You don't have enough days remaining for {selectedBookingYear || 'this year'}
                  </p>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                data-testid="time-off-notes"
                placeholder="e.g., Family vacation, doctor appointment..."
                value={timeOffForm.notes}
                onChange={(e) => setTimeOffForm({...timeOffForm, notes: e.target.value})}
                rows={3}
                className="mt-1"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                data-testid="time-off-cancel-button"
                onClick={() => {
                  setShowTimeOffDialog(false);
                  setTimeOffForm({
                    first_day: '',
                    first_day_type: 'full_day',
                    last_day: '',
                    last_day_type: 'full_day',
                    notes: ''
                  });
                  setCalculatedDays(null);
                  setYearlyAllowance(null);
                  setSelectedBookingYear(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="time-off-submit-button"
                onClick={handleTimeOffSubmit}
                disabled={!timeOffForm.first_day || !timeOffForm.last_day || calculatedDays === null || calculatedDays > (yearlyAllowance ? yearlyAllowance.days_remaining : ((profile?.holiday_entitlement_days || 0) - daysBooked))}
                className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents Section Dialog */}
      <Dialog open={showDocumentsSection} onOpenChange={setShowDocumentsSection}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>My Documents</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Category Tabs */}
            <div className="flex space-x-2 border-b border-gray-200 overflow-x-auto">
              {['Contract', 'Handbook', 'PIP', 'Policies', 'QFM', 'RTW Forms', 'HR Reports'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setDocumentCategory(cat)}
                  className={`px-4 py-2 font-medium whitespace-nowrap transition-all ${
                    documentCategory === cat
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Documents List */}
            {loadingDocuments ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading documents...</p>
              </div>
            ) : documents?.filter(d => d.category === documentCategory).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No documents in this category</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documents.filter(d => d.category === documentCategory).map((doc) => (
                  <div
                    key={doc.id}
                    className="border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start mb-3">
                      <FileText className="w-8 h-8 text-teal-600 mr-3" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 line-clamp-2">{doc.filename}</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {doc.file_type.toUpperCase()} • {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-gray-600 mb-3">
                      Uploaded by: {doc.uploaded_by}
                    </p>

                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => handleViewDocument(doc)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowDocumentsSection(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog open={showViewDocumentDialog} onOpenChange={setShowViewDocumentDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>View Document</DialogTitle>
          </DialogHeader>

          {selectedDocument && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900">{selectedDocument.filename}</h3>
                <p className="text-sm text-gray-600">
                  Category: {selectedDocument.category} • Type: {selectedDocument.file_type.toUpperCase()}
                </p>
                <p className="text-xs text-gray-500">
                  Uploaded by {selectedDocument.uploaded_by} on {new Date(selectedDocument.uploaded_at).toLocaleString('en-GB')}
                </p>
              </div>

              {/* Preview */}
              <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="text-center py-8">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">{selectedDocument.file_type === 'pdf' ? 'PDF Document' : 'Image Document'}</p>
                  <p className="text-sm text-gray-500 mt-2">Click download to view the full document</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowViewDocumentDialog(false)} variant="outline">
              Close
            </Button>
            {selectedDocument && (
              <Button 
                onClick={() => handleDownloadDocument(selectedDocument.id, selectedDocument.filename)}
                className="bg-teal-600 hover:bg-teal-700"
              >
                Download
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Announcement Dialog */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAnnouncement?.title}
              {selectedAnnouncement && (
                <span className={`text-xs px-2 py-1 rounded-full uppercase ${
                  selectedAnnouncement.priority === 'high' ? 'bg-red-500 text-white' :
                  selectedAnnouncement.priority === 'medium' ? 'bg-yellow-500 text-white' :
                  'bg-blue-500 text-white'
                }`}>
                  {selectedAnnouncement.priority}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedAnnouncement && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="bg-gray-100 px-2 py-1 rounded">{selectedAnnouncement.category}</span>
                <span>•</span>
                <span>{new Date(selectedAnnouncement.published_at).toLocaleDateString()}</span>
              </div>
              
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap">{selectedAnnouncement.content}</p>
              </div>

              {selectedAnnouncement.expires_at && (
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  <strong>Expires:</strong> {new Date(selectedAnnouncement.expires_at).toLocaleString()}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowAnnouncementDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Newsletter Dialog */}
      <Dialog open={showNewsletterDialog} onOpenChange={setShowNewsletterDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedNewsletter?.title}</DialogTitle>
          </DialogHeader>

          {selectedNewsletter && (
            <div className="space-y-4">
              {selectedNewsletter.sections.map((section, sIndex) => (
                <div
                  key={section.id || sIndex}
                  style={{
                    backgroundColor: section.backgroundColor,
                    padding: section.padding
                  }}
                  className="rounded-lg"
                >
                  <div className={`grid gap-4 ${
                    section.columnLayout === 1 ? 'grid-cols-1' :
                    section.columnLayout === 2 ? 'grid-cols-2' :
                    section.columnLayout === 3 ? 'grid-cols-3' :
                    'grid-cols-4'
                  }`}>
                    {section.columns.map((column, cIndex) => (
                      <div key={column.id || cIndex}>
                        {column.blocks.map((block, bIndex) => {
                          if (block.type === 'image') {
                            return (
                              <div key={bIndex} className="mb-4">
                                {block.link ? (
                                  <a href={block.link} target="_blank" rel="noopener noreferrer">
                                    <img src={block.url} alt={block.alt} style={{ width: block.width }} className="rounded" />
                                  </a>
                                ) : (
                                  <img src={block.url} alt={block.alt} style={{ width: block.width }} className="rounded" />
                                )}
                              </div>
                            );
                          } else if (block.type === 'heading') {
                            const HeadingTag = block.level;
                            return (
                              <HeadingTag 
                                key={bIndex} 
                                style={{ color: block.color }} 
                                className={`mb-4 font-bold ${
                                  block.level === 'h1' ? 'text-3xl' :
                                  block.level === 'h2' ? 'text-2xl' :
                                  'text-xl'
                                }`}
                              >
                                {block.text}
                              </HeadingTag>
                            );
                          } else if (block.type === 'paragraph') {
                            return <div key={bIndex} className="mb-4 prose" dangerouslySetInnerHTML={{ __html: block.content }} />;
                          } else if (block.type === 'button') {
                            return (
                              <div key={bIndex} className={`mb-4 text-${block.alignment}`}>
                                <a
                                  href={block.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    backgroundColor: block.backgroundColor,
                                    color: block.textColor
                                  }}
                                  className="inline-block px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                                >
                                  {block.text}
                                </a>
                              </div>
                            );
                          } else if (block.type === 'imageWithText') {
                            return (
                              <div key={bIndex} className={`mb-4 flex gap-4 ${block.imagePosition === 'right' ? 'flex-row-reverse' : 'flex-row'}`}>
                                <div className="w-1/3 flex-shrink-0">
                                  {block.link ? (
                                    <a href={block.link} target="_blank" rel="noopener noreferrer">
                                      <img src={block.url} alt={block.alt} className="w-full rounded" />
                                    </a>
                                  ) : (
                                    <img src={block.url} alt={block.alt} className="w-full rounded" />
                                  )}
                                </div>
                                <div className="flex-1 prose" dangerouslySetInnerHTML={{ __html: block.content }} />
                              </div>
                            );
                          } else if (block.type === 'accordion') {
                            return (
                              <details key={bIndex} className="mb-4 rounded-lg overflow-hidden">
                                <summary 
                                  style={{ backgroundColor: block.titleBgColor, color: block.titleColor }}
                                  className="px-4 py-3 cursor-pointer font-semibold"
                                >
                                  {block.title}
                                </summary>
                                <div 
                                  style={{ backgroundColor: block.contentBgColor, color: block.contentColor }}
                                  className="px-4 py-3 prose"
                                  dangerouslySetInnerHTML={{ __html: block.content }}
                                />
                              </details>
                            );
                          }
                          return null;
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowNewsletterDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </div>

      {/* PWA Install Prompt for first-time users */}
      <InstallPrompt 
        isInstallable={isInstallable}
        onInstall={promptInstall}
      />
    </div>
  );
};

export default EmployeePortal;
